import {
  INSURANCE_TYPE_ALIAS,
  INSURANCE_TYPES_ICONS_V2,
} from 'constants/insurance';
import { isLifeReferral } from './marketScan';
import { MARKET_SCAN_AVERAGE_SAVINGS } from 'constants/marketScan';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { IUser } from 'types';

const getSubtitle = (policyType: string) => {
  switch (policyType) {
    case INSURANCE_TYPES_NAMES.PET:
      return 'Exclusive offer';
    case INSURANCE_TYPES_NAMES.TRAVEL:
      return 'Quick quote';
    default:
      if (isLifeReferral(policyType)) {
        return 'Compare options';
      }
      const savings = MARKET_SCAN_AVERAGE_SAVINGS[policyType];
      return savings ? `Avg. savings ${savings}` : '';
  }
};

export const getPolicyTypesToShow = (selectedPolicies: string[] = []) => {
  const otherSelected =
    selectedPolicies.find(
      (p) =>
        ![
          INSURANCE_TYPES_NAMES.VEHICLE,
          INSURANCE_TYPES_NAMES.HOME_CONTENTS,
          INSURANCE_TYPES_NAMES.HOME,
          INSURANCE_TYPES_NAMES.CONTENT,
        ].includes(p),
    ) || INSURANCE_TYPES_NAMES.HEALTH;

  return [
    {
      key: INSURANCE_TYPES_NAMES.VEHICLE,
      title: INSURANCE_TYPES_NAMES.VEHICLE,
      subtitle: `Avg. savings ${MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.VEHICLE]
        }`,
      image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.VEHICLE],
    },
    {
      key: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
      title: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
      subtitle: `Avg. savings ${MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.HOME_CONTENTS]
        }`,
      image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.HOME_CONTENTS],
    },
    {
      key: otherSelected,
      title:
        otherSelected === INSURANCE_TYPES_NAMES.LIFE
          ? 'Life insurance'
          : INSURANCE_TYPE_ALIAS[otherSelected] || otherSelected,
      subtitle: getSubtitle(otherSelected),
      image: INSURANCE_TYPES_ICONS_V2[otherSelected],
    },
  ];
};

export const policyTypesToShow = [
  {
    key: INSURANCE_TYPES_NAMES.VEHICLE,
    title: INSURANCE_TYPES_NAMES.VEHICLE,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.VEHICLE],
    image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.VEHICLE],
  },
  {
    key: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
    title: INSURANCE_TYPES_NAMES.HOME_CONTENTS,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.HOME_CONTENTS],
    image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.HOME_CONTENTS],
  },
  {
    key: INSURANCE_TYPES_NAMES.CONTENT,
    title: INSURANCE_TYPES_NAMES.CONTENT,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.CONTENT],
    image: INSURANCE_TYPES_ICONS_V2[INSURANCE_TYPES_NAMES.CONTENT],
  },
];

// Types that can be compared via Market Scan
const COMPARABLE_TYPES = [
  INSURANCE_TYPES_NAMES.VEHICLE,
  INSURANCE_TYPES_NAMES.MOTORCYCLE,
  INSURANCE_TYPES_NAMES.HOME,
  INSURANCE_TYPES_NAMES.CONTENT,
  INSURANCE_TYPES_NAMES.HOME_CONTENTS,
  INSURANCE_TYPES_NAMES.LANDLORD,
  INSURANCE_TYPES_NAMES.LIFE,
  INSURANCE_TYPES_NAMES.MBI,
  INSURANCE_TYPES_NAMES.RV,
];

const DEFAULT_OPTIONS = [
  INSURANCE_TYPES_NAMES.VEHICLE,
  INSURANCE_TYPES_NAMES.HOME,
  INSURANCE_TYPES_NAMES.CONTENT,
  INSURANCE_TYPES_NAMES.LIFE,
];

// The 5 default card types; order drives the defaults branch and backfill.
const COMMON_TYPES = [
  INSURANCE_TYPES_NAMES.VEHICLE,
  INSURANCE_TYPES_NAMES.HOME,
  INSURANCE_TYPES_NAMES.CONTENT,
  INSURANCE_TYPES_NAMES.LIFE,
  INSURANCE_TYPES_NAMES.MBI,
];

/**
 * Builds an array of policy type card data objects from an array of type keys.
 * Titles use the display alias where one exists (MBI → "Mechanical Breakdown").
 */
const buildPolicyTypeCards = (types: string[]) => {
  return types.map((type) => ({
    key: type,
    title: INSURANCE_TYPE_ALIAS[type] || type,
    subtitle: MARKET_SCAN_AVERAGE_SAVINGS[type] || '',
    image: INSURANCE_TYPES_ICONS_V2[type],
  }));
};

/**
 * Returns 4 unique policy type cards for the waiting screen.
 * - Returning users (loginsCount > 1 — newUser flips false too early to use)
 *   or users with ≤ 1 comparable onboarding pick: COMMON_TYPES minus the
 *   current scan, first 4.
 * - Otherwise: onboarding picks, then COMMON_TYPES backfill, then
 *   DEFAULT_OPTIONS top-up (current/scanned allowed back in) to reach 4.
 */
export const getComparablePolicyTypesToShow = (
  user: IUser | undefined,
  currentPolicyType?: string,
  alreadyScannedTypes: string[] = []
) => {
  const scannedTypesSet = new Set(alreadyScannedTypes);

  // Onboarding selection stripped to comparable types (drops Pet/Travel/Health).
  const comparableOnboarding = (user?.insuranceTypes || []).filter((type) =>
    COMPARABLE_TYPES.includes(type)
  );
  const onboardingSet = new Set(comparableOnboarding);

  // Missing/zero loginsCount counts as a first session (personalized path).
  const isReturningUser = (user?.loginsCount || 0) > 1;
  if (isReturningUser || comparableOnboarding.length <= 1) {
    return buildPolicyTypeCards(
      COMMON_TYPES.filter((type) => type !== currentPolicyType).slice(0, 4)
    );
  }

  // Build personalized options
  const selectedOptions = new Set<string>();

  const isScannedOrCurrent = (type: string) =>
    type === currentPolicyType || scannedTypesSet.has(type);

  // 1. Onboarding remainder — pick order, excluding current/scanned types.
  comparableOnboarding.forEach((type) => {
    if (selectedOptions.size < 4 && !isScannedOrCurrent(type)) {
      selectedOptions.add(type);
    }
  });

  // 2. Common backfill — canonical order, excluding onboarding and current/scanned.
  COMMON_TYPES.forEach((type) => {
    if (
      selectedOptions.size < 4 &&
      !onboardingSet.has(type) &&
      !isScannedOrCurrent(type) &&
      !selectedOptions.has(type)
    ) {
      selectedOptions.add(type);
    }
  });

  // 3. Default fill — top up to 4 from defaults (dedup only), so we always show 4.
  DEFAULT_OPTIONS.forEach((type) => {
    if (selectedOptions.size < 4 && !selectedOptions.has(type)) {
      selectedOptions.add(type);
    }
  });

  return buildPolicyTypeCards(Array.from(selectedOptions));
};

